/**
 * Datasheet (Virtuoso) cho 1 ngân hàng — bám form Access `frmFuturesQryBankCashIn` /
 * `frmFuturesQryBankCashInBIDV` (2 form share `FuturesQryBankCashIn` query, layout giống nhau).
 *
 * Component này CHỈ render bảng + toolbar pagination/search/columns. Bank-selector + nút
 * New/Edit/Delete nằm ở `FuturesBankCashInTab` (đúng như container `frmFuturesMainSubFormBank`).
 *
 * Migrated to /table-style template (2026-05-26): FilterBar + useTableSearch +
 * per-column ▽ filter Excel-style. BE trả full rows (pageSize=1e6) → local filter.
 */
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material'
import { useMemo, useState, type CSSProperties, type ReactNode } from 'react'
import { TableVirtuoso } from 'react-virtuoso'

import BrandState from '../../../components/BrandState'
import ResizeHandle from '../../../components/ResizeHandle'
import { useIsMobile } from '../../../hooks/useResponsive'
import {
  ColumnFilterIcon,
  ColumnFilterPopover,
  FilterBar,
  FilterButton,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  filterBarBtnSx,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
  type ColumnFilterAnchorPosition,
} from '../../../components/dataList'
import { useColumnWidths } from '../../../hooks/useColumnWidths'
import {
  HIDE_ON_MOBILE_INLINE,
  HIDE_ON_TABLET_INLINE,
  TABLE_PAGINATION_COMPACT,
} from '../../../theme/responsive'
import {
  FUTURES_ALL_ROWS_PAGE_SIZE,
  useGetFuturesBankCashQuery,
} from '../../../store/api/futuresControlApi'
import { FuturesColumnsMenu } from '../FuturesColumnsMenu'
import { useFuturesColumns } from '../useFuturesColumns'
import {
  BANK_CASH_COLUMNS,
  BANK_CASH_TOTAL_COLUMNS,
  formatBankCashCell,
  formatBankCashTotal,
  type BankCashColumnDef,
} from './bankCashInColumns'
import type { BankCashInRow, BankName } from './BankCashInDialog'

type GridRow = BankCashInRow & { id: string; date: string; bank: string }

/** Cột freeze khi scroll ngang — Date (cột định danh theo ngày). */
const FROZEN_KEYS: ReadonlySet<string> = new Set(['date'])
const FROZEN_FALLBACK_WIDTH_DESKTOP: Record<string, number> = { date: 120 }
const FROZEN_FALLBACK_WIDTH_MOBILE: Record<string, number> = { date: 125 }

interface Props {
  bank: BankName
  /** Storage key tách riêng giữa view VTB/MBB và view BIDV để mỗi variant nhớ visibility độc lập. */
  columnsStorageKey: string
  /** Slot hiển thị nút action (do Tab cha truyền xuống — Tab nắm selected row + dialog state). */
  actionsSlot?: ReactNode
  /** Callback khi user click 1 row — Tab cha cập nhật selection. */
  onSelectionChange: (row: BankCashInRow | null) => void
  /** Tab cha bơm xuống id row đang chọn (để highlight). */
  selectedKey: string | null
}

const rowKey = (r: BankCashInRow): string =>
  `${String(r.bank ?? '')}|${String(r.date ?? '').slice(0, 10)}`

export default function BankCashInDataSheet({
  bank,
  columnsStorageKey,
  actionsSlot,
  onSelectionChange,
  selectedKey,
}: Props) {
  /* ── Filter state (template /table-style) ───────────────────────── */
  const [searchTerm, setSearchTerm] = useState('')
  const filterPopover = useFilterPopover<'search'>()
  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: string
    position: ColumnFilterAnchorPosition
  } | null>(null)

  const [page, setPage] = useState(0)
  // -1 = «All» (khớp rowsPerPageOptions). KHÔNG init = FUTURES_ALL_ROWS_PAGE_SIZE
  // vì giá trị đó không nằm trong options → MUI báo out-of-range value cho Select.
  const [pageSize, setPageSize] = useState(-1)

  /* ── Server query — BE return full dataset, local filter ───────── */
  const { data, isLoading, isFetching, error, refetch } = useGetFuturesBankCashQuery({
    bank,
    page: 1,
    pageSize: FUTURES_ALL_ROWS_PAGE_SIZE,
  })

  const columnsCtrl = useFuturesColumns({
    mode: 'bank-cash',
    allColumns: BANK_CASH_COLUMNS,
    storageKey: columnsStorageKey,
  })
  const { visibleColumns, openMenu: openColMenu } = columnsCtrl
  const { getWidth, startResize } = useColumnWidths(`futures.bank-cash-in.${bank}`)
  const isMobile = useIsMobile()
  const FROZEN_FALLBACK_WIDTH = isMobile ? FROZEN_FALLBACK_WIDTH_MOBILE : FROZEN_FALLBACK_WIDTH_DESKTOP

  /* ── Base rows ──────────────────────────────────────────────────── */
  const baseRows = useMemo((): GridRow[] => {
    const items = data?.items ?? []
    return items.map((r, idx) => {
      const rec = r as Record<string, unknown>
      const dk = String(rec.date ?? '').slice(0, 10)
      return {
        ...(rec as BankCashInRow),
        id: `${bank}-${idx}-${dk}`,
        date: dk,
        bank: String(rec.bank ?? bank),
      }
    })
  }, [data?.items, bank])

  /* ── Filter pipeline: search (toàn cột) ∧ per-column ▽ ─────────── */
  const filteredRows = useMemo(
    () =>
      baseRows.filter(
        (r) =>
          rowMatchesSearch(r, searchTerm, BANK_CASH_COLUMNS) &&
          rowMatchesAllColumnFilters(r, columnFilters.filters),
      ),
    [baseRows, searchTerm, columnFilters.filters],
  )

  /* ── Pagination clamp ───────────────────────────────────────────── */
  const totalRows = filteredRows.length
  const pagedRows = useMemo(
    () =>
      pageSize === -1
        ? filteredRows
        : filteredRows.slice(page * pageSize, page * pageSize + pageSize),
    [filteredRows, page, pageSize],
  )

  const totals = useMemo(() => {
    const out = new Map<string, number | null>()
    for (const col of visibleColumns) {
      if (!BANK_CASH_TOTAL_COLUMNS.has(col.key)) {
        out.set(col.key, null)
        continue
      }
      let sum = 0
      let n = 0
      for (const row of pagedRows) {
        const v = (row as unknown as Record<string, unknown>)[col.key]
        if (typeof v === 'number' && Number.isFinite(v)) {
          sum += v
          n += 1
        }
      }
      out.set(col.key, n > 0 ? sum : null)
    }
    return out
  }, [pagedRows, visibleColumns])

  /* ── Filter-bar helpers ─────────────────────────────────────────── */
  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount =
    (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    setPage(0)
    filterPopover.close()
    onSelectionChange(null)
  }

  /* ── Column popover meta ────────────────────────────────────────── */
  const popoverColumn = useMemo(
    () =>
      colPopover
        ? BANK_CASH_COLUMNS.find((c) => c.key === colPopover.key) ?? null
        : null,
    [colPopover],
  )
  const popoverKind = useMemo(
    () =>
      popoverColumn
        ? inferColumnFilterKind(popoverColumn as never, baseRows)
        : 'text',
    [popoverColumn, baseRows],
  )
  const popoverOptions = useMemo(
    () =>
      popoverKind === 'checkboxes' && colPopover
        ? distinctColumnValues(baseRows, colPopover.key)
        : undefined,
    [popoverKind, colPopover, baseRows],
  )

  const minTableWidth = visibleColumns.length * 110
  const gridKey = `${page}:${pageSize}:${filteredRows.length}:${bank}`

  const selectedRowId = useMemo(() => {
    if (!selectedKey) return null
    return pagedRows.find((r) => rowKey(r) === selectedKey)?.id ?? null
  }, [pagedRows, selectedKey])

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        // Bảng FILL hết flex còn lại (parent bounded) → bỏ khoảng trống dưới bảng.
        '& > .app-dataframe': { flex: 1, minHeight: 0, height: 'auto' },
      }}
    >
      <FilterBar
        hasActiveFilters={hasActiveFilters}
        onClearAll={clearAllFilters}
        activeFilterCount={activeFilterCount}
        rightSlot={actionsSlot}
      >
        <FilterButton
          label="Search"
          active={!!searchTerm.trim()}
          onClick={(e) => filterPopover.open('search', e)}
        />
        <Button
          size="small"
          variant="outlined"
          endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
          onClick={(e) => openColMenu(e.currentTarget)}
          sx={filterBarBtnSx}
          title="Columns"
        >
          <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Columns</Box>
          <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>Cols</Box>
        </Button>
      </FilterBar>

      <Divider sx={{ mb: 0.5 }} />

      <FilterPopover
        open={filterPopover.state !== null}
        anchorEl={filterPopover.state?.anchor ?? null}
        onClose={() => filterPopover.close()}
      >
        {filterPopover.isOpen('search') && (
          <SearchFilterContent
            value={searchTerm}
            onChange={(v) => {
              setSearchTerm(v)
              setPage(0)
            }}
            placeholder="Date, currency, amounts, FX, user…"
            onClear={() => filterPopover.close()}
          />
        )}
      </FilterPopover>

      <FuturesColumnsMenu controller={columnsCtrl} />

      <ColumnFilterPopover
        open={colPopover !== null}
        anchorPosition={colPopover?.position ?? null}
        onClose={() => setColPopover(null)}
        label={popoverColumn?.label ?? ''}
        kind={popoverKind === 'none' ? 'text' : popoverKind}
        value={colPopover ? columnFilters.filters[colPopover.key] : undefined}
        onChange={(v) => {
          if (!colPopover) return
          columnFilters.setFilter(colPopover.key, v)
          setPage(0)
        }}
        options={popoverOptions}
      />

      <Box className="app-dataframe">
        <Box
          className="app-dataframe-toolbar"
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 0.5, flexWrap: 'wrap', minHeight: 24, py: 0 }}
        >
          <Stack direction="row" alignItems="center" spacing={0.4} sx={{ flexShrink: 0 }}>
            {(isLoading || isFetching) && <CircularProgress size={15} />}
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              Total: {totalRows}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}>
              Loaded: {baseRows.length}
            </Typography>
            <TablePagination
              component="div"
              count={totalRows}
              page={pageSize === -1 ? 0 : page}
              onPageChange={(_, newPage) => {
                setPage(newPage)
                onSelectionChange(null)
              }}
              rowsPerPage={pageSize}
              onRowsPerPageChange={(e) => {
                setPageSize(Number(e.target.value))
                setPage(0)
                onSelectionChange(null)
              }}
              rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
              labelRowsPerPage="Rows"
              sx={TABLE_PAGINATION_COMPACT}
            />
          </Stack>
        </Box>

        <Box sx={{ flex: 1, minHeight: 320, overflow: 'hidden' }}>
          {error ? (
            <BrandState
              tone="error"
              title="Could not load cash-in"
              description="Try again or contact support if the error persists."
              action={
                <Button size="small" variant="outlined" onClick={() => void refetch()}>
                  Retry
                </Button>
              }
            />
          ) : isLoading && pagedRows.length === 0 ? (
            <BrandState tone="loading" title="Loading…" description="Please wait." />
          ) : pagedRows.length === 0 ? (
            <BrandState title="No data" description="Pick a bank, adjust search, or add a row." />
          ) : (
            <TableVirtuoso
              key={gridKey}
              style={{ height: '100%' }}
              data={pagedRows}
              fixedHeaderContent={() => (
                <TableRow>
                  {visibleColumns.map((col) => {
                    const isFrozen = FROZEN_KEYS.has(col.key)
                    const w = getWidth(col.key) ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[col.key] : undefined)
                    const headerStyle: CSSProperties = {}
                    if (w) { headerStyle.width = w; headerStyle.minWidth = w; headerStyle.maxWidth = w }
                    if (isFrozen) {
                      headerStyle.position = 'sticky'
                      headerStyle.top = 0
                      headerStyle.left = 0
                      headerStyle.zIndex = 100
                    }
                    return (
                      <TableCell
                        key={col.key}
                        align={(col as BankCashColumnDef).align}
                        className="app-table-header-cell"
                        style={Object.keys(headerStyle).length ? headerStyle : undefined}
                        sx={{
                          whiteSpace: 'nowrap',
                          // Cho phép bôi đen + copy tên cột (header).
                          userSelect: 'text',
                          ...(isFrozen ? {} : { position: 'relative' }),
                        }}
                      >
                        {col.label}
                        <ColumnFilterIcon
                          active={columnFilters.isActive(col.key)}
                          onClick={(position) =>
                            setColPopover({ key: col.key, position })
                          }
                          ariaLabel={`Filter ${col.label}`}
                        />
                        <ResizeHandle onMouseDown={(e) => startResize(col.key, e)} />
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              fixedFooterContent={() => (
                <TableRow>
                  {visibleColumns.map((col, index) => {
                    const isFrozen = FROZEN_KEYS.has(col.key)
                    const total = totals.get(col.key)
                    const val = total == null ? '' : formatBankCashTotal(total, col as BankCashColumnDef)
                    const w = getWidth(col.key) ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[col.key] : undefined)
                    return (
                      <TableCell
                        key={col.key}
                        align={(col as BankCashColumnDef).align}
                        className={isFrozen ? 'app-sticky-col' : undefined}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={{
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                          py: 0.5,
                          bgcolor: 'background.paper',
                          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {index === 0 ? 'Total' : val}
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              itemContent={(_: number, row: GridRow) =>
                visibleColumns.map((col) => {
                  const cd = col as BankCashColumnDef
                  const isFrozen = FROZEN_KEYS.has(col.key)
                  const val = formatBankCashCell((row as unknown as Record<string, unknown>)[col.key], cd)
                  const w = getWidth(col.key) ?? (isFrozen ? FROZEN_FALLBACK_WIDTH[col.key] : undefined)
                  const wrap = getWidth(col.key) != null
                  if (wrap) {
                    return (
                      <TableCell
                        key={col.key}
                        align={cd.align}
                        className={isFrozen ? 'app-sticky-col' : undefined}
                        style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                        sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere', py: 0.5, verticalAlign: 'top' }}
                      >
                        <Box component="span" className="app-table-cell-wrap">
                          {val || '-'}
                        </Box>
                      </TableCell>
                    )
                  }
                  return (
                    <TableCell
                      key={col.key}
                      align={cd.align}
                      className={isFrozen ? 'app-sticky-col' : undefined}
                      style={w ? { width: w, minWidth: w, maxWidth: w } : undefined}
                      sx={{ whiteSpace: 'nowrap', py: 0.5, overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'top' }}
                    >
                      <Box
                        component="span"
                        className="app-table-cell-text"
                        title={val || '-'}
                        sx={{ display: 'inline-block', width: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}
                      >
                        {val || '-'}
                      </Box>
                    </TableCell>
                  )
                })
              }
              components={{
                Table: (props: any) => (
                  <Table {...props} size="small" stickyHeader sx={{ minWidth: minTableWidth, width: '100%' }} />
                ),
                TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
                TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
                TableRow: (props: any) => {
                  const idx = Number((props as Record<string, unknown>)['data-item-index'])
                  const r = Number.isFinite(idx) ? pagedRows[idx] : null
                  const rid = r?.id ?? null
                  return (
                    <TableRow
                      {...props}
                      hover
                      selected={rid != null && rid === selectedRowId}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => {
                        if (!r) return
                        onSelectionChange(r)
                      }}
                    />
                  )
                },
              }}
            />
          )}
        </Box>
      </Box>
    </Box>
  )
}
